import React, { type FunctionComponent, useEffect, useRef, useMemo } from 'react';
import { Field } from '@atlaskit/form';
import LockLockedIcon from '@atlaskit/icon/core/migration/lock-locked--lock';
import LockUnlockedIcon from '@atlaskit/icon/core/migration/lock-unlocked--unlock';
import PeopleIcon from '@atlaskit/icon/glyph/people';
import { Box, Flex, Inline, Stack, xcss } from '@atlaskit/primitives';
import Select, { components, type OptionProps, type ControlProps } from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { type AccessLevel, FREE_ACCESS_LEVEL } from '@atlassian/jira-shared-types';
import type { AccessType } from '../../types';
import messages from './messages';
import { AccessWrapper, ICON_SIZE } from './styled';
import type { Props, ReactComponentState } from './types';

export type OptionType = (AccessType | undefined) & {
	label: string;
	value: AccessLevel;
};

const getIcon = (icon: string) => {
	const localProps = {
		label: '',
		color: token('color.icon.subtle', colors.N100),
		LEGACY_size: ICON_SIZE,
		spacing: 'spacious' as const,
	};

	switch (icon) {
		case 'open':
			return <LockUnlockedIcon {...localProps} />;
		case 'limited':
			return (
				// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
				<PeopleIcon
					label={localProps.label}
					primaryColor={localProps.color}
					size={localProps.LEGACY_size}
				/>
			);
		case 'private':
			return <LockLockedIcon {...localProps} />;
		default:
			return (
				// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
				<PeopleIcon
					label={localProps.label}
					primaryColor={localProps.color}
					size={localProps.LEGACY_size}
				/>
			);
	}
};

export const CustomControl: FunctionComponent<ControlProps<OptionType>> = ({
	children,
	...props
}) => {
	const selectedOption = props.getValue();
	const selectedIcon =
		selectedOption && selectedOption[0] != null ? getIcon(selectedOption[0].iconName) : null;

	return (
		<components.Control {...props}>
			<Inline alignBlock="center" testId="project-access.field.option">
				<Box paddingInlineStart="space.100">{selectedIcon}</Box>
			</Inline>
			{children}
		</components.Control>
	);
};

export const CustomOption: FunctionComponent<OptionProps<OptionType>> = (props) => {
	const { isDisabled, data } = props;
	const { description, iconName, displayName } = data;

	return (
		<components.Option {...props}>
			<Flex
				alignItems="center"
				xcss={[isDisabled ? optionWrapperDisabledStyles : optionWrapperEnabledStyles]}
			>
				<Inline alignInline="start" testId="project-access.field.option">
					<Flex gap="space.050" alignItems="center" xcss={iconContainerStyles}>
						{getIcon(iconName)}
					</Flex>
					<Stack xcss={contentContainerStyles}>
						<Box>{displayName}</Box>
						<Box xcss={fg('hix-5072_use_tokens_in_css') ? descriptionStyles : descriptionStylesOld}>
							{description}
						</Box>
					</Stack>
				</Inline>
			</Flex>
		</components.Option>
	);
};

type ProjectAccessSelectProps = Props & ReactComponentState;

export const ProjectAccessSelect = ({
	onChange,
	isDisabled,
	currentAccessType,
	validateRequired,
	accessTypes,
	hasFetchDataError,
	onError,
	onAccessTypesLoaded,
	onFirstChange,
	requiredAccess,
	isLoading,
	DisabledAccessLevel,
	onWillMount,
	isOpen,
}: ProjectAccessSelectProps) => {
	const { formatMessage } = useIntl();
	const prevAccessTypes = useRef<AccessType[]>(accessTypes);
	const prevAccessType = useRef<string | undefined>(undefined);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => onWillMount(), []);

	const accessTypeOptions: OptionType[] = useMemo(
		() =>
			accessTypes.map((accessType: AccessType) => ({
				label: accessType.displayName,
				...accessType,
			})),
		[accessTypes],
	);

	const selected = useMemo(() => {
		const value = accessTypes?.find((type: AccessType) => type.value === currentAccessType?.value);
		return value ? { label: value.displayName, ...value } : undefined;
	}, [accessTypes, currentAccessType]);

	// Downgraded projects keep their original access level, but cannot be modified
	const initiallySelectedNotFree = useMemo(
		() => (selected && selected.value !== FREE_ACCESS_LEVEL) ?? true,
		[selected],
	);

	useEffect(() => {
		if (onError && hasFetchDataError) {
			onError?.();
		}
	}, [hasFetchDataError, onError]);

	useEffect(() => {
		if (onAccessTypesLoaded && prevAccessTypes.current.length === 0 && accessTypes.length > 0) {
			onAccessTypesLoaded();
		}
		prevAccessTypes.current = accessTypes;
	}, [accessTypes, onAccessTypesLoaded, onError]);

	// If the access type is set for the first time, call onFirstChange
	useEffect(() => {
		if (currentAccessType && !prevAccessType.current) {
			onFirstChange(currentAccessType.value);
		}
		prevAccessType.current = currentAccessType?.value;
	}, [currentAccessType, onFirstChange]);

	// If a value is set, check if it's not free and call onChange
	useEffect(() => {
		if (!initiallySelectedNotFree && selected) {
			onChange(selected);
		}
	}, [initiallySelectedNotFree, onChange, selected]);

	const renderField = () => (
		<Select<OptionType>
			isDisabled={isDisabled}
			isLoading={isLoading}
			required={requiredAccess}
			isInvalid={!currentAccessType && validateRequired}
			options={accessTypeOptions}
			aria-label={formatMessage(messages.projectAccessTitle)}
			onChange={(option) => {
				if (option?.value) {
					onChange(option);
				}
			}}
			classNamePrefix="project-access"
			value={selected}
			isSearchable={false}
			placeholder={formatMessage(messages.projectAccessPlaceholder)}
			components={{ Control: CustomControl, Option: CustomOption }}
			menuIsOpen={isOpen}
			testId="project-access.field.select"
		/>
	);

	const renderDisabledFields = () => {
		// If the field is disabled and free selected, show a tooltip informing the user why this value is set.
		if (isDisabled && initiallySelectedNotFree) {
			return (
				<ContextualAnalyticsData sourceName="projectDetails" sourceType={SCREEN}>
					<Tooltip
						content={formatMessage(messages.downgradedProjectAccessMessage)}
						onShow={(event) => {
							fireUIAnalytics(event, 'accessLevelFreeTier');
						}}
					>
						{renderField()}
					</Tooltip>
				</ContextualAnalyticsData>
			);
		}

		// If field is disabled and the selected access level is not free, show informational message.
		// if the access level is set and project is downgraded we want to preserve the existing
		// access level and inform the user why it can't be changed.
		if (isDisabled && !initiallySelectedNotFree) {
			return (
				<ContextualAnalyticsData sourceName="projectDetailsDisabledAccessLevel" sourceType={SCREEN}>
					<FireScreenAnalytics />
					<DisabledAccessLevel />
				</ContextualAnalyticsData>
			);
		}

		return renderField();
	};

	return isLoading || accessTypes.length > 0 ? (
		<AccessWrapper data-pollinator-access-level-picker data-testid="project-access.field.wrapper">
			<Field
				name="project-access-field"
				isRequired={initiallySelectedNotFree ? requiredAccess : false}
				label={formatMessage(messages.projectAccessTitle)}
			>
				{() => renderDisabledFields()}
			</Field>
		</AccessWrapper>
	) : null;
};

const optionWrapperEnabledStyles = xcss({
	opacity: 1,
	cursor: 'pointer',
});

const optionWrapperDisabledStyles = xcss({
	opacity: 0.5,
	cursor: 'not-allowed',
});

const iconContainerStyles = xcss({
	flexGrow: '0',
});

const contentContainerStyles = xcss({
	flexGrow: '1',
	marginLeft: 'space.100',
	marginRight: 'space.200',
});

const descriptionStylesOld = xcss({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '11px',
	lineHeight: token('space.200', '16px'),
	marginTop: 'space.100',
	color: 'color.text.subtle',
});

const descriptionStyles = xcss({
	font: token('font.body.small'),
	lineHeight: token('space.200', '16px'),
	marginTop: 'space.100',
	color: 'color.text.subtle',
});
